import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { useAuth } from '../auth/AuthProvider.js';

/**
 * Where Cognito sends the browser back - AUTH-004.
 *
 * `auth-stack.ts` has registered `${appUrl}/auth/callback` since AUTH-002 and
 * nothing has ever served it: a real login landed on the 404 screen. This is the
 * other half of a contract that has been deployed for days.
 */
export function AuthCallback() {
  const { completeSignIn } = useAuth();
  const navigate = useNavigate();
  const [failure, setFailure] = useState<string | null>(null);
  // StrictMode mounts effects twice in development, and an authorization code is
  // single-use - the second redemption is `invalid_grant`. Redeeming once per
  // mount is not optional here.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    completeSignIn(globalThis.location.search)
      .then((returnTo) => navigate(returnTo, { replace: true }))
      .catch((error: unknown) => {
        setFailure(error instanceof Error ? error.message : String(error));
      });
  }, [completeSignIn, navigate]);

  if (failure !== null) {
    return (
      <section className="mx-auto max-w-md p-8">
        <h1 className="text-lg font-semibold">Sign-in did not complete</h1>
        <p className="mt-2 text-sm text-muted-foreground">{failure}</p>
        <a className="mt-4 inline-block text-sm underline" href="/">
          Start again
        </a>
      </section>
    );
  }

  return (
    <p role="status" className="p-8 text-sm">
      Finishing sign-in…
    </p>
  );
}
